import { memberIsAdmin } from '~/shared/utils/memberUtils.js'

/**
 * Loads system config and wires the maintenance-mode watcher.
 *
 * This plugin used to `return { name, hooks: { 'app:created': ... } }` where
 * the hook called `setupSubscriptions()` on the members and subkrewes stores.
 * It never ran: Nuxt's `applyPlugin` destructures only `{ provide }` from a
 * *function* plugin's return value, and reads `hooks` off the plugin object
 * beforehand. Both store methods have since been removed — their purpose is
 * served by `subscribeToCurrentMember` (wired via `useCoreSubscriptions`) and
 * by `assignCaptainToSubKrewe` updating both stores at the point of mutation.
 */
export default defineNuxtPlugin(() => {
  // Tests mock stores directly, so skip the live wiring.
  const isTestEnv = process.env.VITEST === 'true'
    || process.env.NODE_ENV === 'test'
    || process.env.TEST === 'true'

  if (isTestEnv) return

  setupLiveStores()
})

function setupLiveStores() {
  const systemConfigStore = useSystemConfigStore()

  // System config, live from the start (parade year, maintenance mode).
  // Firestore rule allows public read of system/config so this works before
  // auth, and admins toggling maintenance mode reach everyone at once.
  systemConfigStore.loadConfig().catch((error) => {
    console.error('[setup-stores] Failed to load system config:', error)
  })

  if (import.meta.client) {
    // React to maintenance flips:
    //  - ON  -> push non-exempt users to /maintenance
    //  - OFF -> bounce users off /maintenance back to /
    watch(
      () => systemConfigStore.config?.maintenanceMode,
      async (enabled, prev) => {
        if (enabled === prev) return
        const route = useRoute()

        if (enabled) {
          if (route.path === '/maintenance') return
          const user = useCurrentUser().value
          if (!user) return
          const member = await useCurrentMember(user.uid)
          if (!member) return
          const isEvenflow = user.email?.endsWith('@evenflow.nl') ?? false
          const isBetaTester = member.beta_tester === true
          if (memberIsAdmin(member) || isEvenflow || isBetaTester) return
          await navigateTo('/maintenance')
        }
        else if (route.path === '/maintenance') {
          await navigateTo('/')
        }
      },
    )
  }
}
